import type { FieldValues, Path, UseFormSetError } from "react-hook-form";

/**
 * A mapping of common English field validation messages to their Hebrew translations.
 * This is used to provide user-friendly error messages in Hebrew based on the server's response.
 * If a message is not recognized, the original English message will be displayed.
 */
const FIELD_VALIDATION_MESSAGE_MAP: Record<string, string> = {
  "These credentials do not match our records.": "פרטי ההתחברות אינם נכונים.",
  "The account number does not exist.": "מספר חשבון לקוח אינו קיים.",
  "The given data was invalid.": "יש לתקן את השדות המסומנים.",
};


/**
 * Converts a field validation message to Hebrew.
 * If the message is not recognized, it returns the original message.
 * @param message english validation message
 * @returns string
 */
function toHebrewFieldMessage(message: string): string {
  return FIELD_VALIDATION_MESSAGE_MAP[message] ?? message;
}


/**
 * Maps server field errors to react-hook-form's setError function.
 * If knownFields is provided, only errors for those fields will be set. Other errors will be set as root.serverError.
 * If knownFields is not provided, all field errors will be set directly.
 * @param setError 
 * @param fieldErrors 
 * @param knownFields 
 * @returns 
 */
export function mapFieldErrorsToForm<T extends FieldValues>(
  setError: UseFormSetError<T>,
  fieldErrors?: Record<string, string[]>,
  knownFields?: ReadonlyArray<Path<T>>
): void {

  if (!fieldErrors) {
    return;
  }

  for (const [field, messages] of Object.entries(fieldErrors)) {
    const message = toHebrewFieldMessage(messages[0] ?? "שגיאה");
    const castPath = field as Path<T>;

    if (!knownFields || knownFields.includes(castPath)) {
      setError(castPath, { type: "server", message });
      continue;
    }

    setError("root.serverError" as Path<T>, { type: "server", message });
  }
}
